import type { App } from "vue";
import { createAcl, defineAclRules } from "vue-simple-acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import { DisplayPermission } from "~/core/models/DisplayPermission.enum";
import { useUserSession } from "~/modules/user/stores/useUserSession";
import { router } from "~/plugins/router";

/**
 * Mapping between the permissions defined in backend and the ACL roles used in frontend.
 * An ACL role is granted if at least one of the backend permissions is granted.
 */
const rolesFromPermission: {
  role: ACLRoles;
  permissions: DisplayPermission[];
}[] = [
  {
    role: ACLRoles.CAN_READ_ORG,
    permissions: [
      DisplayPermission.CAN_READ_ORG,
      DisplayPermission.CAN_WRITE_ORG,
    ],
  },
  {
    role: ACLRoles.CAN_WRITE_ORG,
    permissions: [DisplayPermission.CAN_WRITE_ORG],
  },
  {
    role: ACLRoles.CAN_READ_USER,
    permissions: [
      DisplayPermission.CAN_READ_USER,
      DisplayPermission.CAN_WRITE_USER,
    ],
  },
  {
    role: ACLRoles.CAN_WRITE_USER,
    permissions: [DisplayPermission.CAN_WRITE_USER],
  },
  {
    role: ACLRoles.CAN_READ_FEATURE,
    permissions: [
      DisplayPermission.CAN_READ_FEATURE,
      DisplayPermission.CAN_WRITE_FEATURE,
    ],
  },
  {
    role: ACLRoles.CAN_WRITE_FEATURE,
    permissions: [DisplayPermission.CAN_WRITE_FEATURE],
  },
  {
    role: ACLRoles.CAN_READ_DEVICE,
    permissions: [
      DisplayPermission.CAN_READ_DEVICE,
      DisplayPermission.CAN_WRITE_DEVICE,
    ],
  },
  {
    role: ACLRoles.CAN_WRITE_DEVICE,
    permissions: [DisplayPermission.CAN_WRITE_DEVICE],
  },
  {
    role: ACLRoles.CAN_READ_FIRMWARE,
    permissions: [
      DisplayPermission.CAN_READ_FIRMWARE,
      DisplayPermission.CAN_WRITE_FIRMWARE,
    ],
  },
  {
    role: ACLRoles.CAN_WRITE_FIRMWARE,
    permissions: [DisplayPermission.CAN_WRITE_FIRMWARE],
  },
  {
    role: ACLRoles.CAN_READ_CONFIG,
    permissions: [
      DisplayPermission.CAN_READ_CONFIG,
      DisplayPermission.CAN_WRITE_CONFIG,
    ],
  },
  {
    role: ACLRoles.CAN_WRITE_CONFIG,
    permissions: [DisplayPermission.CAN_WRITE_CONFIG],
  },
  {
    role: ACLRoles.CAN_MANAGE_FIRMWARE_OPERATION,
    permissions: [DisplayPermission.CAN_MANAGE_FIRMWARE_OPERATION],
  },
  {
    role: ACLRoles.CAN_MANAGE_CONFIGURATION_OPERATION,
    permissions: [DisplayPermission.CAN_MANAGE_CONFIGURATION_OPERATION],
  },
  {
    role: ACLRoles.CAN_MANAGE_CERTIFICATE_OPERATION,
    permissions: [DisplayPermission.CAN_MANAGE_CERTIFICATE_OPERATION],
  },
  {
    role: ACLRoles.CAN_MANAGE_OPTIONS_OPERATION,
    permissions: [DisplayPermission.CAN_MANAGE_OPTIONS_OPERATION],
  },
  {
    role: ACLRoles.CAN_READ_SECTOR,
    permissions: [
      DisplayPermission.CAN_READ_SECTOR,
      DisplayPermission.CAN_WRITE_SECTOR,
    ],
  },
  {
    role: ACLRoles.CAN_WRITE_SECTOR,
    permissions: [DisplayPermission.CAN_WRITE_SECTOR],
  },
  {
    role: ACLRoles.CAN_READ_APPLICATION,
    permissions: [
      DisplayPermission.CAN_READ_APPLICATION,
      DisplayPermission.CAN_WRITE_APPLICATION,
    ],
  },
  {
    role: ACLRoles.CAN_WRITE_APPLICATION,
    permissions: [DisplayPermission.CAN_WRITE_APPLICATION],
  },
  {
    role: ACLRoles.CAN_READ_LICENSE,
    permissions: [
      DisplayPermission.CAN_READ_LICENSE,
      DisplayPermission.CAN_WRITE_LICENSE,
    ],
  },
  {
    role: ACLRoles.CAN_WRITE_LICENSE,
    permissions: [DisplayPermission.CAN_WRITE_LICENSE],
  },
  {
    role: ACLRoles.CAN_MANAGE_DEVICE_FLEET_LOG,
    permissions: [DisplayPermission.CAN_MANAGE_DEVICE_FLEET_LOG],
  },
  {
    role: ACLRoles.CAN_MANAGE_CYBER_LOG,
    permissions: [DisplayPermission.CAN_MANAGE_CYBER_LOG],
  },
  {
    role: ACLRoles.CAN_MANAGE_SYSTEM_LOG,
    permissions: [DisplayPermission.CAN_MANAGE_SYSTEM_LOG],
  },
  {
    role: ACLRoles.CAN_MANAGE_DEVICE_LOG,
    permissions: [DisplayPermission.CAN_MANAGE_DEVICE_LOG],
  },
  {
    role: ACLRoles.CAN_READ_FIELD_PERMISSION,
    permissions: [DisplayPermission.CAN_READ_FIELD_PERMISSION],
  },
  {
    role: ACLRoles.CAN_WRITE_FIELD_PERMISSION,
    permissions: [DisplayPermission.CAN_WRITE_FIELD_PERMISSION],
  },
  {
    role: ACLRoles.CAN_READ_RELATION,
    permissions: [DisplayPermission.CAN_READ_RELATION],
  },
  {
    role: ACLRoles.CAN_WRITE_RELATION,
    permissions: [DisplayPermission.CAN_WRITE_RELATION],
  },
  {
    role: ACLRoles.CAN_READ_CERTIFICATE,
    permissions: [DisplayPermission.CAN_READ_CERTIFICATE],
  },
  {
    role: ACLRoles.CAN_WRITE_CERTIFICATE,
    permissions: [DisplayPermission.CAN_WRITE_CERTIFICATE],
  },
];

export default {
  install: (app: App) => {
    const user = computed(() => useUserSession().$state.userSession);

    const rules = () =>
      defineAclRules<typeof user.value>((setRule) =>
        rolesFromPermission.forEach((t) => {
          setRule(t.role, (user) =>
            t.permissions.some((permission) =>
              (user?.permissions ?? []).includes(permission),
            ),
          );
        }),
      );

    app.use(
      createAcl({
        user,
        rules,
        router,
      }),
    );
  },
};
